
import { Options, Vue } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

interface ErrorInfo {
  message: string;
  stack?: string;
  componentStack?: string;
}

@Options({})
export default class ErrorBoundary extends Vue {
  @Prop({ default: null })
  onError?: (error: Error, errorInfo: ErrorInfo) => void;

  @Prop({ default: null })
  onRetry?: () => Promise<void> | void;

  @Prop({ default: 'An unexpected error occurred. Please try refreshing the page.' })
  fallbackMessage!: string;

  private hasError = false;
  private errorInfo: ErrorInfo | null = null;
  private showDetails = false;
  private retrying = false;
  private retryCount = 0;
  private maxRetries = 3;

  // Vue 3 error handling
  errorCaptured(error: Error, instance: any, info: string): boolean {
    this.captureError(error, { 
      message: error.message, 
      stack: error.stack,
      componentStack: info 
    });
    
    // Return false to prevent the error from propagating further
    return false;
  }

  private captureError(error: Error, errorInfo: ErrorInfo): void {
    this.hasError = true;
    this.errorInfo = errorInfo;
    
    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Call custom error handler if provided
    if (this.onError) {
      try {
        this.onError(error, errorInfo);
      } catch (handlerError) {
        console.error('Error in custom error handler:', handlerError);
      }
    }
  }

  private async handleRetry(): Promise<void> {
    if (this.retryCount >= this.maxRetries) {
      this.$emit('max-retries-reached');
      return;
    }

    this.retrying = true;
    this.retryCount++;

    try {
      if (this.onRetry) {
        await this.onRetry();
      }
      
      // Reset error state on successful retry
      this.hasError = false;
      this.errorInfo = null;
      this.showDetails = false;
      
      this.$emit('retry-success');
    } catch (retryError) {
      console.error('Retry failed:', retryError);
      this.$emit('retry-failed', retryError);
    } finally {
      this.retrying = false;
    }
  }

  private handleReset(): void {
    this.hasError = false;
    this.errorInfo = null;
    this.showDetails = false;
    this.retryCount = 0;
    this.$emit('reset');
  }

  get userFriendlyMessage(): string {
    if (!this.errorInfo) return this.fallbackMessage;

    const message = this.errorInfo.message.toLowerCase();
    
    // Provide user-friendly messages for common errors
    if (message.includes('network') || message.includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    if (message.includes('timeout')) {
      return 'The request is taking longer than expected. Please try again.';
    }
    
    if (message.includes('permission') || message.includes('unauthorized')) {
      return 'You don\'t have permission to access this resource. Please contact your administrator.';
    }
    
    if (message.includes('not found') || message.includes('404')) {
      return 'The requested resource could not be found. It may have been moved or deleted.';
    }
    
    return this.fallbackMessage;
  }

  get errorDetails(): string {
    if (!this.errorInfo) return '';
    
    return `Error: ${this.errorInfo.message}\n\n` +
           `Stack Trace:\n${this.errorInfo.stack || 'No stack trace available'}\n\n` +
           `Component Stack:\n${this.errorInfo.componentStack || 'No component stack available'}\n\n` +
           `Retry Count: ${this.retryCount}/${this.maxRetries}`;
  }
}
